import React, { createContext, useContext, useEffect, useState } from 'react';
import { authService } from '../services/authService';
import { setAnalyticsUserId, setAnalyticsUserProperties, trackCustomEvent } from '../firebase';

const AuthContext = createContext();

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Función para limpiar errores
  const clearError = () => setError(null);

  // Función para manejar errores de autenticación
  const handleAuthError = (error) => {
    let errorMessage = 'Error de autenticación';
    
    switch (error.code) {
      case 'auth/user-not-found':
        errorMessage = 'Usuario no encontrado';
        break;
      case 'auth/wrong-password':
        errorMessage = 'Contraseña incorrecta';
        break;
      case 'auth/email-already-in-use':
        errorMessage = 'El email ya está en uso';
        break;
      case 'auth/weak-password':
        errorMessage = 'La contraseña es muy débil';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Email inválido';
        break;
      case 'auth/too-many-requests':
        errorMessage = 'Demasiados intentos. Intenta más tarde';
        break;
      default:
        errorMessage = error.message || 'Error desconocido';
    }
    
    setError(errorMessage);
    
    // Track error en Analytics
    trackCustomEvent('auth_error', {
      error_code: error.code,
      error_message: errorMessage
    });
  };

  // Función para actualizar información del usuario en Analytics
  const updateAnalyticsUserInfo = (user) => {
    if (user) {
      setAnalyticsUserId(user.uid);
      setAnalyticsUserProperties({
        email: user.email,
        display_name: user.displayName,
        email_verified: user.emailVerified,
        provider: user.providerData[0]?.providerId || 'email',
        creation_time: user.metadata.creationTime,
        last_sign_in_time: user.metadata.lastSignInTime
      });
    } else {
      setAnalyticsUserId(null);
      setAnalyticsUserProperties({
        email: null,
        display_name: null,
        email_verified: false,
        provider: null
      });
    }
  };

  // Función de login con Google
  const loginWithGoogle = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await authService.loginWithGoogle();
      
      // Track successful Google login
      trackCustomEvent('login_success', {
        method: 'google',
        user_id: result.user.uid,
        provider: 'google.com'
      });
      
      return result;
    } catch (error) {
      handleAuthError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Función de login
  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      
      const userCredential = await authService.loginWithEmail(email, password);
      
      // Track successful login
      trackCustomEvent('login_success', {
        method: 'email',
        user_id: userCredential.user.uid
      });
      
      return userCredential;
    } catch (error) {
      handleAuthError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Función de registro
  const register = async (email, password, displayName) => {
    try {
      setLoading(true);
      setError(null);
      
      const userCredential = await authService.registerWithEmail(email, password, displayName);
      
      // Track successful registration
      trackCustomEvent('sign_up_success', {
        method: 'email',
        user_id: userCredential.user.uid
      });
      
      return userCredential;
    } catch (error) {
      handleAuthError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Función de logout
  const logout = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Track logout antes de hacer logout
      trackCustomEvent('logout', {
        user_id: user?.uid
      });
      
      await authService.logout();
      
    } catch (error) {
      handleAuthError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Función para resetear contraseña
  const resetPassword = async (email) => {
    try {
      setLoading(true);
      setError(null);
      
      await authService.resetPassword(email);
      
      // Track password reset
      trackCustomEvent('password_reset_requested', {
        email: email
      });
      
      return true;
    } catch (error) {
      handleAuthError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Efecto para escuchar cambios en el estado de autenticación
  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged((user) => {
      setUser(user);
      setLoading(false);
      
      // Actualizar Analytics
      updateAnalyticsUserInfo(user);
      
      // Track auth state change
      if (user) {
        trackCustomEvent('auth_state_changed', {
          state: 'authenticated',
          user_id: user.uid
        });
      } else {
        trackCustomEvent('auth_state_changed', {
          state: 'unauthenticated'
        });
      }
    });

    return unsubscribe;
  }, []);

  const value = {
    user,
    loading,
    error,
    login,
    loginWithGoogle,
    register,
    logout,
    resetPassword,
    clearError,
    // Propiedades útiles derivadas
    isAuthenticated: !!user,
    isEmailVerified: user?.emailVerified || false,
    userDisplayName: user?.displayName || user?.email || 'Usuario',
    userEmail: user?.email || null,
    userId: user?.uid || null
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
